/* screens/analytics/TableSearch.jsx — поиск по табличным данным Аналитики:
   поле (`TableSearch`) и его состояние (`useTableSearch`).

   Задача владельца: «взять существующий поиск из „Ликвидности“ и добавить
   аналогичный механизм в ABC, „Продажи“ и „Инвентаризацию“ — единообразный,
   универсальный». Поэтому здесь нет ничего нового по виду: тот же SearchInput
   UI-кита (иконка, крестик, высота ряда), что стоял в «Ликвидности», плюс одно
   правило сравнения на все вкладки (lib/table/tableSearch.js: без регистра,
   «ё» = «е» — так же ищет и серверная выгрузка).

   ГДЕ СТОИТ. Верхняя панель отчёта (`.an-toolbar`) после периода/разреза: поиск —
   главный способ добраться до конкретной строки, и прятать его в свёрнутые на
   телефоне «Фильтры» нельзя. На узком экране — своим рядом во всю ширину
   (analytics-redesign.css, `.an-search`).

   ЧТО СЧИТАЕТСЯ. Активный поиск — один фильтр в счётчике `ReportFilters`
   (`count`) и ключ `q` в выгрузке (`query`). Свёрнутый фильтр без счётчика —
   отчёт, показывающий не то, что человек думает. */
import React from 'react';
import { SearchInput } from '../../components/ui/Field.jsx';
import { filterBySearch, normalizeSearch } from '../../lib/table/tableSearch.js';

const { useState, useMemo, useCallback } = React;

/** Состояние поиска таблицы.
 *
 * value  — строка в поле (меняется на каждую букву: поле отвечает сразу);
 * needle — нормализованный запрос; active / count — для счётчика фильтров;
 * query  — что уходит в выгрузку ключом `q` (обрезанная строка как есть:
 *          нормализует сервер, тем же правилом);
 * filter(rows, get) — строки, чьё название `get(row)` подходит. Без запроса
 *          возвращает тот же массив, функция стабильна, пока запрос тот же. */
export function useTableSearch(initial = '') {
  const [value, setValue] = useState(initial);
  const needle = useMemo(() => normalizeSearch(value), [value]);
  const filter = useCallback((rows, get) => filterBySearch(rows, needle, get), [needle]);
  const clear = useCallback(() => setValue(''), [setValue]);
  return {
    value, setValue, needle, filter, clear,
    active: needle !== '',
    count: needle ? 1 : 0,
    query: String(value == null ? '' : value).trim(),
  };
}

/** Поле поиска по таблице.
 *
 * search — состояние из `useTableSearch` (обычный клиентский поиск); либо
 * value/onChange напрямую. onDebouncedChange — для серверного поиска («Движение
 * товара»): запрос уходит после паузы набора, а не на каждую букву; крестик
 * зовёт его сразу. label — подпись для скринридера (по умолчанию —
 * плейсхолдер без многоточия). */
export function TableSearch({ search, value, onChange, onDebouncedChange, delay,
                              placeholder = 'Поиск…', label, className = '' }) {
  const v = search ? search.value : value;
  const set = search ? search.setValue : onChange;
  return (
    <SearchInput className={'an-search' + (className ? ' ' + className : '')}
      value={v == null ? '' : v} onChange={set} onDebouncedChange={onDebouncedChange}
      delay={delay} placeholder={placeholder}
      aria-label={label || String(placeholder).replace(/[….]+$/, '')} />
  );
}
